import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { listDevices, revokeDevice, type DeviceItem } from '@/lib/operatorApi';
import { relativeTime } from '@/lib/format';

export default function Devices() {
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [onlyActive, setOnlyActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listDevices({ onlyActive, limit: 300 })
      .then((r) => setDevices(r.results))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [onlyActive]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Отозвать устройство? Оно перестанет работать немедленно.')) return;
    try {
      await revokeDevice(id);
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Устройства"
        title="Подключённые кассы и терминалы"
        description="Все устройства всех арендаторов. Можно отозвать токен и принудительно разлогинить."
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={13} strokeWidth={1.75} />
            Обновить
          </button>
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4 flex items-center gap-3">
          <label className="flex items-center gap-2 text-[13px] text-ink/70">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
            />
            Только активные (не отозванные)
          </label>
        </div>

        {/* Desktop: таблица */}
        <div className="card hidden md:block">
          <table className="table-grid">
            <thead>
              <tr>
                <th>Устройство</th>
                <th>Арендатор</th>
                <th>Платформа</th>
                <th>IP</th>
                <th>Статус</th>
                <th className="text-right">Последняя активность</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id}>
                  <td className="font-medium">{d.name}</td>
                  <td>
                    <Link to={`/tenants/${d.tenantId}`} className="hover:underline">
                      {d.tenantName}
                    </Link>
                  </td>
                  <td className="text-ink/70">{d.platform}</td>
                  <td className="font-mono text-[12px] text-ink/60">{d.lastIp ?? '—'}</td>
                  <td>
                    {d.revoked ? (
                      <span className="text-[12px] text-red-600">Отозвано</span>
                    ) : d.online ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-green-600" />
                        <span className="text-[12px]">Онлайн</span>
                      </span>
                    ) : (
                      <span className="text-[12px] text-ink/50">Оффлайн</span>
                    )}
                  </td>
                  <td className="text-right font-mono text-[12px] text-ink/60">
                    {d.lastSeenAt ? relativeTime(d.lastSeenAt) : '—'}
                  </td>
                  <td className="text-right">
                    {!d.revoked && (
                      <button
                        className="text-[12px] text-red-600 hover:underline"
                        onClick={() => handleRevoke(d.id)}
                      >
                        Отозвать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && devices.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-ink/50">
                    Устройств не найдено
                  </td>
                </tr>
              )}
              {loading && devices.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: карточки */}
        <ul className="card divide-y divide-line md:hidden">
          {devices.map((d) => (
            <li key={d.id} className="px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium text-ink">{d.name}</div>
                  <Link
                    to={`/tenants/${d.tenantId}`}
                    className="mt-0.5 block truncate text-[12px] text-ink/60 hover:underline"
                  >
                    {d.tenantName}
                  </Link>
                </div>
                <div className="shrink-0">
                  {d.revoked ? (
                    <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-red-600">
                      Отозвано
                    </span>
                  ) : d.online ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-green-600" />
                      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-green-700">
                        Онлайн
                      </span>
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink/50">
                      Оффлайн
                    </span>
                  )}
                </div>
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-[12px]">
                <dt className="text-ink/50">Платформа</dt>
                <dd className="text-right text-ink/80">{d.platform}</dd>
                <dt className="text-ink/50">IP</dt>
                <dd className="text-right font-mono text-ink/60">{d.lastIp ?? '—'}</dd>
                <dt className="text-ink/50">Активность</dt>
                <dd className="text-right font-mono text-ink/60">
                  {d.lastSeenAt ? relativeTime(d.lastSeenAt) : '—'}
                </dd>
              </dl>

              {!d.revoked && (
                <div className="mt-3 flex justify-end">
                  <button
                    className="text-[12px] font-medium text-red-600 hover:underline"
                    onClick={() => handleRevoke(d.id)}
                  >
                    Отозвать устройство
                  </button>
                </div>
              )}
            </li>
          ))}
          {!loading && devices.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/50">
              Устройств не найдено
            </li>
          )}
          {loading && devices.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
          )}
        </ul>
      </div>
    </div>
  );
}
